import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Package, AlertCircle, Download } from 'lucide-react';
import { StaffAllScannedItemsHeader } from './StaffAllScannedItemsHeader';
import { StaffAllScannedItemsFilters } from './StaffAllScannedItemsFilters';
import { StaffAllScannedItemsStats } from './StaffAllScannedItemsStats';
import { StaffProductsList } from './StaffProductsList';
import { FastTrackProductEntry } from '@/components/FastTrackProductEntry';
import { useToast } from '@/hooks/use-toast';

export interface AllScannedProduct {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
  orderId: string;
  orderSupplier: string;
  orderDate: string;
  createdAt: string;
  scannedBy?: string;
}

export interface FilterState {
  searchQuery: string;
  selectedSupplier: string;
  dateRange: { start: string; end: string };
  statusFilter: string;
}

export const StaffAllScannedItemsView = () => {
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    selectedSupplier: '',
    dateRange: { start: '', end: '' },
    statusFilter: 'all'
  });
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const { toast } = useToast();

  // Fetch all scanned products
  const { 
    data: allProducts = [], 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['all-scanned-products'],
    queryFn: async (): Promise<AllScannedProduct[]> => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          barcode,
          name,
          boxes,
          units_per_box,
          photos,
          has_credit,
          order_id,
          created_at,
          orders(supplier_name, delivery_date)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return (data || []).map(item => ({
        id: item.id,
        barcode: item.barcode || '',
        name: item.name || 'Unnamed Product',
        boxes: item.boxes || 0,
        unitsPerBox: item.units_per_box || 0,
        photos: item.photos || [],
        hasCredit: item.has_credit || false,
        posStatus: undefined, // Will be determined by system lookup
        catalogName: undefined,
        orderId: item.order_id,
        orderSupplier: item.orders?.supplier_name || 'Unknown Supplier',
        orderDate: item.orders?.delivery_date || '',
        createdAt: item.created_at,
        scannedBy: undefined
      }));
    }
  });

  // Apply filters
  const filteredProducts = allProducts.filter(product => {
    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const matches = 
        product.name.toLowerCase().includes(query) ||
        product.barcode.toLowerCase().includes(query) ||
        (product.catalogName && product.catalogName.toLowerCase().includes(query)) ||
        product.orderSupplier.toLowerCase().includes(query) ||
        product.orderId.toLowerCase().includes(query);
      
      if (!matches) return false;
    }

    // Supplier filter
    if (filters.selectedSupplier && filters.selectedSupplier !== 'all') {
      if (product.orderSupplier !== filters.selectedSupplier) return false;
    }

    // Date range filter
    if (filters.dateRange.start) {
      const productDate = new Date(product.createdAt);
      const startDate = new Date(filters.dateRange.start);
      if (productDate < startDate) return false;
    }
    
    if (filters.dateRange.end) {
      const productDate = new Date(product.createdAt);
      const endDate = new Date(filters.dateRange.end);
      endDate.setHours(23, 59, 59, 999); // End of day
      if (productDate > endDate) return false;
    }

    // Status filter
    if (filters.statusFilter !== 'all') {
      if (filters.statusFilter === 'new' && product.posStatus === 'found') return false;
      if (filters.statusFilter === 'existing' && product.posStatus !== 'found') return false;
      if (filters.statusFilter === 'credit' && !product.hasCredit) return false;
    }

    return true;
  });

  // Get unique suppliers for filter
  const suppliers = Array.from(new Set(allProducts.map(p => p.orderSupplier))).sort();

  // Create or get a general order for unassigned products
  const createGeneralOrder = async () => {
    const { data: existingOrder } = await supabase
      .from('orders')
      .select('id')
      .eq('id', 'GENERAL-INVENTORY')
      .single();

    if (existingOrder) {
      return 'GENERAL-INVENTORY';
    }

    const { error } = await supabase
      .from('orders')
      .insert({
        id: 'GENERAL-INVENTORY',
        supplier_name: 'General Inventory',
        delivery_date: new Date().toISOString().split('T')[0],
        status: 'active',
        product_count: 0
      });

    if (error) {
      console.error('Error creating general order:', error);
      throw error;
    }

    return 'GENERAL-INVENTORY';
  };

  // Quick add functionality
  const handleQuickAdd = () => {
    setIsQuickAddOpen(true);
  };

  const handleAddProduct = async (product: {
    barcode: string;
    name: string;
    boxes: number;
    unitsPerBox: number;
    photos: string[];
    creationSource: 'scan';
  }): Promise<boolean> => {
    try {
      const orderId = await createGeneralOrder();
      
      const payload: any = {
        order_id: orderId,
        barcode: product.barcode,
        name: product.name,
        boxes: product.boxes,
        units_per_box: product.unitsPerBox,
        photos: product.photos,
        creation_source: product.creationSource
      };

      const { error } = await supabase
        .from('products')
        .insert(payload);

      if (error) throw error;

      // Refresh the products list by refetching
      refetch();
      
      toast({
        title: "Product Added",
        description: `${product.name} has been added to inventory`,
      });

      setIsQuickAddOpen(false);
      return true;
    } catch (error) {
      console.error('Error adding product:', error);
      toast({
        title: "Error",
        description: "Failed to add product. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Export functionality
  const handleExport = () => {
    const csvContent = [
      ['Name', 'Barcode', 'Supplier', 'Order ID', 'Boxes', 'Units/Box', 'Status', 'Scanned Date'].join(','),
      ...filteredProducts.map(product => [
        `"${product.name}"`,
        product.barcode,
        `"${product.orderSupplier}"`,
        product.orderId,
        product.boxes,
        product.unitsPerBox,
        product.posStatus === 'found' ? 'Existing' : 'New',
        new Date(product.createdAt).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scanned-items-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <Package className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-bold text-foreground mb-2">Loading Scanned Items...</h2>
          <p className="text-muted-foreground">Please wait while we fetch all your scanned products.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Error Loading Items</h2>
          <p className="text-muted-foreground mb-4">Unable to load scanned products.</p>
          <div className="space-y-2">
            <button 
              onClick={() => refetch()} 
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Try Again
            </button>
            <button 
              onClick={() => window.history.back()}
              className="w-full px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-background">
      <div className="w-full px-2 md:px-4 lg:container lg:mx-auto py-2 md:py-6">
        {/* Header */}
        <StaffAllScannedItemsHeader 
          totalItems={allProducts.length}
          filteredItems={filteredProducts.length}
          onExport={handleExport}
          onQuickAdd={handleQuickAdd}
        />

        {/* Stats */}
        <StaffAllScannedItemsStats products={allProducts} />

        {/* Filters */}
        <StaffAllScannedItemsFilters 
          filters={filters}
          onFiltersChange={setFilters}
          suppliers={suppliers}
        />

        {/* Products List */}
        {filteredProducts.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="text-center py-8">
              <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {allProducts.length === 0 ? "No Scanned Items Found" : "No Items Match Filters"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {allProducts.length === 0 
                  ? "No products have been scanned yet."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <StaffProductsList products={filteredProducts.map(p => ({
            id: p.id,
            barcode: p.barcode,
            name: p.name,
            boxes: p.boxes,
            unitsPerBox: p.unitsPerBox,
            photos: p.photos,
            hasCredit: p.hasCredit,
            posStatus: p.posStatus,
            catalogName: p.catalogName
          }))} />
        )}
      </div>

      <FastTrackProductEntry
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onAddProduct={handleAddProduct}
      />
    </div>
  );
};